import CasTextField, { ICasTextField } from '../CasTextField/CasTextField';

type CasWideTextFilterProps = ICasTextField & {
  // The toolbar width, wide enough for the label; the Filters drawer spans its own width.
  minWidth: number;
  // CasTable hands these to every `component` filter; a text box has no use for them.
  includeAllOption?: boolean;
  includeNoneOption?: boolean;
  includeEmptyOption?: boolean;
  // CasFilterChips renders a component filter in display mode for its chip text.
  mode?: 'display';
};

/*
  A text filter for a label too long for a plain CasTable text filter. Use it as a
  `component` filter, with `minWidth` (and any other CasTextField prop, e.g. `debounce`) in
  `componentProps`. CasTable leaves a text filter at MUI's default (~200px), which cuts a
  long label off with an ellipsis whenever the box is empty, and gives it no way to ask for
  more. (Sizing text filters to their label inside CasTable would widen other pages'
  filters too.)
*/
const CasWideTextFilter = ({
  minWidth,
  includeAllOption,
  includeNoneOption,
  includeEmptyOption,
  mode,
  fullWidth,
  sx,
  ...props
}: CasWideTextFilterProps) => {
  if (mode === 'display') return <>{props.value}</>;

  return (
    <CasTextField
      {...props}
      fullWidth={fullWidth}
      // The Filters drawer passes its full width through. On the toolbar, CasTable's
      // label-based estimate (8px a character) leaves a long label too little room to
      // survive another machine's font rendering, so the caller names the width.
      sx={fullWidth ? sx : { minWidth }}
      InputLabelProps={{
        sx: {
          // Once a value is typed the theme floats the label at 16px scaled to 75%, but
          // still clips it to the width of the box it no longer sits in, which cuts a long
          // label off. Let it run to the width it is drawn at, as newer MUI versions do.
          '&.MuiInputLabel-shrink': { maxWidth: 'calc(133% - 32px)' },
        },
      }}
    />
  );
};

export default CasWideTextFilter;
